// EventCard.tsx
import { Event } from "./EventGrid";
import { Category } from "./EventPage";
import { Calendar, IndianRupee } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface EventCardProps {
  event: Event;
  index: number;
  category: Category;
}

const styles = `
  @keyframes card-enter {
    0% { 
      transform: translateY(30px) scale(0.92); 
      opacity: 0; 
      filter: blur(8px);
    }
    100% { 
      transform: translateY(0) scale(1); 
      opacity: 1; 
      filter: blur(0);
    }
  }

  .closed-overlay {
    opacity: 0;
  }

  .event-card:hover .closed-overlay {
    opacity: 1;
  }
`;

export const EventCard = ({ event, index }: EventCardProps) => {
  const navigate = useNavigate();

  // Detect Hackathon (registrations closed)
  const isRegistrationClosed =
  event.name.toLowerCase().includes("hackathon") ||
  event.name.toLowerCase().includes("webinar") ||
  event.name.toLowerCase().includes("seminar") ||
  event.name.toLowerCase().includes("workshop") ||
  event.name.toLowerCase().includes("chess") ||
  event.name.toLowerCase().includes("carrom");


  const getEventId = (eventName: string) =>
    eventName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const handleCardClick = () => {
    if (isRegistrationClosed) return;
    navigate(`/event/${getEventId(event.name)}`);
  };

  return (
    <>
      <style>{styles}</style>

      <div
        className="event-card"
        onClick={handleCardClick}
        style={{
          position: "relative",
          background: "var(--color-surface)",
          borderRadius: "8px",
          overflow: "hidden",
          cursor: isRegistrationClosed ? "not-allowed" : "pointer",
          transition: "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)",
          animation: `card-enter 0.6s ease-out ${index * 0.08}s backwards`,
          boxShadow:
            "0 4px 20px rgba(var(--color-primary-rgb, 59, 130, 246), 0.06)",
          border: "1px solid var(--color-border)",
          height: "480px",
          display: "flex",
          flexDirection: "column",
          opacity: isRegistrationClosed ? 0.85 : 1,
        }}
        onMouseEnter={(e) => {
          if (isRegistrationClosed) return;
          const card = e.currentTarget;
          card.style.transform = "translateY(-8px) scale(1.02)";
          card.style.borderColor = "var(--color-primary)";
          card.style.boxShadow =
            "0 20px 50px rgba(var(--color-primary-rgb, 59, 130, 246), 0.3)";
        }}
        onMouseLeave={(e) => {
          if (isRegistrationClosed) return;
          const card = e.currentTarget;
          card.style.transform = "translateY(0) scale(1)";
          card.style.borderColor = "var(--color-border)";
          card.style.boxShadow =
            "0 4px 20px rgba(var(--color-primary-rgb, 59, 130, 246), 0.06)";
        }}
      >
        {/* Image */}
        <div
          style={{
            width: "100%",
            height: "340px",
            background: event.imageUrl
              ? `url('${event.imageUrl}') center/cover no-repeat`
              : "linear-gradient(145deg, #111, #222)",
            position: "relative",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.75))",
            }}
          />
        </div>

        {/* Content */}
        <div
          style={{
            padding: "16px",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          {/* Title & Price */}
          <div style={{ display: "flex", gap: "12px" }}>
            <h3
              style={{
                fontWeight: 700,
                fontSize: "1.1rem",
                margin: 0,
                flex: 1,
              }}
            >
              {event.name}
            </h3>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                padding: "6px 10px",
                borderRadius: "6px",
                background: "rgba(220,38,38,0.1)",
                border: "1px solid rgba(220,38,38,0.3)",
                fontSize: "0.85rem",
                fontWeight: 600,
              }}
            >
              {event.price === "" ? (
                "FREE"
              ) : (
                <>
                  <IndianRupee size={14} />
                  {event.price}
                </>
              )}
            </div>
          </div>

          {/* Date */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "0.85rem",
              color: "var(--color-text-muted)",
            }}
          >
            <Calendar size={16} />
            {event.date}
          </div>
        </div>

        {/* Registrations Closed Overlay */}
        {isRegistrationClosed && (
          <div
            className="closed-overlay"
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.65)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "opacity 0.3s ease",
              pointerEvents: "none",
            }}
          >
            <span
              style={{
                padding: "10px 18px",
                borderRadius: "999px",
                background: "rgba(220,38,38,0.15)",
                border: "1px solid rgba(220,38,38,0.5)",
                color: "#f87171",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Registrations Closed
            </span>
          </div>
        )}
      </div>
    </>
  );
};
